/**
 * PostContent — Renders post/comment text with:
 *  - @[Name](userId) tokens → blue chip
 *  - URLs (http/https, www.) → clickable links opening in new tab
 *  - newlines preserved
 */
import React from 'react';

// Token pattern: @[Name](userId) OR URLs
// Group 1: mention name, Group 2: mention userId
// Group 3: URL match
const TOKEN_RE = /@\[([^\]]+)\]\(([\w-]+)\)|((?:https?:\/\/|www\.)[^\s<]+[^\s.,;:!?<\)\]\}'"])/g;

const sanitizeUrl = (raw) => {
  if (!raw) return null;
  const trimmed = raw.replace(/[.,;:!?\)\]\}'"]+$/, '');
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^www\./i.test(trimmed)) return `https://${trimmed}`;
  return null;
};

const shortUrl = (url) => {
  try {
    const u = new URL(url);
    const path = u.pathname === '/' ? '' : u.pathname;
    return `${u.hostname.replace(/^www\./, '')}${path}`.slice(0, 60);
  } catch { return url.slice(0, 60); }
};

export const PostContent = ({ text, className = '' }) => {
  if (!text) return null;
  const parts = [];
  let lastIndex = 0;
  let match;
  TOKEN_RE.lastIndex = 0;
  while ((match = TOKEN_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }
    if (match[1] !== undefined) {
      parts.push({ type: 'mention', name: match[1], userId: match[2] });
    } else if (match[3]) {
      parts.push({ type: 'url', value: match[3] });
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push({ type: 'text', value: text.slice(lastIndex) });

  return (
    <p className={`text-slate-800 text-sm whitespace-pre-wrap leading-relaxed break-words ${className}`} data-testid="post-content">
      {parts.map((p, i) => {
        if (p.type === 'mention') {
          return (
            <span key={i} className="inline-flex items-center px-1.5 py-0.5 mx-0.5 rounded bg-sky-50 border border-sky-100 text-sky-700 font-semibold text-[13px]" data-testid={`mention-${p.userId}`}>
              @{p.name}
            </span>
          );
        }
        if (p.type === 'url') {
          const href = sanitizeUrl(p.value);
          if (!href) return <span key={i}>{p.value}</span>;
          return (
            <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:text-sky-700 underline break-all" data-testid="post-link">
              {shortUrl(href)}
            </a>
          );
        }
        return <span key={i}>{p.value}</span>;
      })}
    </p>
  );
};

export default PostContent;
